import * as React from "react";
import { FC, useCallback } from "react";
import NewWindow, { INewWindowProps } from "react-new-window";
import createCache, { type EmotionCache } from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { TooltipContainerContext } from "../providers/TooltipContainerProvider";

interface IPopoutWindowContext {
    pullStylesFromParentWindow: () => void;
    closeWindow: () => void;
    /** The pop-out's own window — use `window.document.body` as the MUI portal `container` for
     *  Popper/Tooltip/Menu so they render inside the pop-out rather than the parent window. */
    window: Window | null;
}

export const PopoutWindowContext: React.Context<IPopoutWindowContext> = React.createContext(null);
export const usePopoutWindowContext: () => IPopoutWindowContext = () => React.useContext(PopoutWindowContext);

// The one <style> element this component owns for mirrored parent styles — kept id'd so a re-pull only
// replaces it and never wipes emotion's own injected style tags.
const MIRRORED_STYLES_ID = "popout-mirrored-styles";

/**
 * Wrapper for react-new-window that makes MUI (and Emotion) render correctly inside the pop-out.
 *
 * Two mechanisms:
 *  1. An **Emotion cache** whose container is the pop-out's `<head>`, provided via `CacheProvider`, so
 *     MUI components inject their styles directly into the pop-out (rather than the parent window).
 *  2. A **mirror of the parent's stylesheets** (app CSS + theme variables, which are not emotion) copied
 *     into the pop-out `<head>`; refreshable via the context for late-loading styles.
 *
 * Consumers that portal (Popper/Tooltip/Menu) should set their `container` to `context.window.document.body`.
 *
 * Same public interface as react-new-window.
 */
const PopoutWindow: FC<INewWindowProps> = ({ onOpen, onUnload, children, ...props }) => {
    const [context, setContext] = React.useState<IPopoutWindowContext>(null);
    const [cache, setCache] = React.useState<EmotionCache | null>(null);
    const windowRef = React.useRef<Window>(null);

    // Use the pop-out window's body as the container for tooltips/popovers...
    const tooltipContainerRef = React.useRef<HTMLDivElement | (() => HTMLDivElement)>(
        () => windowRef.current?.document.body as unknown as HTMLDivElement
    );

    const compatOnOpen = useCallback(
        (childWindow: Window) => {
            windowRef.current = childWindow;

            // MUI/Emotion insert their runtime styles into this cache's container — point it at the
            // pop-out's head so components are styled in the pop-out itself.
            const popoutCache = createCache({ key: "popout", container: childWindow.document.head });
            setCache(popoutCache);

            const pullStylesFromParentWindow = () => {
                const childHead = childWindow.document.head;

                // Replace only our mirrored <style>; leave emotion's `data-emotion` tags intact.
                childHead.querySelector(`#${MIRRORED_STYLES_ID}`)?.remove();

                const allParentStyles = Array.from(document.styleSheets).flatMap(s => {
                    try {
                        return Array.from(s.cssRules).map(r => r.cssText);
                    } catch (e) {
                        console.warn(e);
                        return [];
                    }
                });

                const styleElement = childWindow.document.createElement("style");
                styleElement.id = MIRRORED_STYLES_ID;
                styleElement.innerHTML = allParentStyles.join("\n");
                childHead.appendChild(styleElement);

                // Copy active theme class from parent window to the child...
                childWindow.document.body.className = document.body.className;
            };

            // Immediately pull the current styles from the parent, with the ability to update these later via the Context...
            pullStylesFromParentWindow();
            setContext({
                pullStylesFromParentWindow,
                closeWindow: () => childWindow.close(),
                window: childWindow,
            });

            onOpen?.(childWindow);
        },
        [onOpen]
    );

    const compatOnUnload = useCallback(() => {
        onUnload?.();
    }, [onUnload]);

    // Automatically close the child window when the parent window is closed...
    React.useEffect(() => {
        const onParentWindowUnload = () => {
            if (windowRef.current) {
                windowRef.current.close();
            }
        };

        window.addEventListener("beforeunload", onParentWindowUnload);
        return () => {
            window.removeEventListener("beforeunload", onParentWindowUnload);
        };
    }, []);

    return (
        <PopoutWindowContext.Provider value={context}>
            <NewWindow onOpen={compatOnOpen} onUnload={compatOnUnload} {...props}>
                {cache ? (
                    <CacheProvider value={cache}>
                        <TooltipContainerContext.Provider value={tooltipContainerRef}>
                            {children}
                        </TooltipContainerContext.Provider>
                    </CacheProvider>
                ) : null}
            </NewWindow>
        </PopoutWindowContext.Provider>
    );
};

export default PopoutWindow;
